import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Backdrop,
  Tooltip,
  ClickAwayListener,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useAuth } from "oidc-react";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchProductModual from "./SearchProductModual";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DurationDisplay from "../components/DurationDisplay";
import { useClarityEventTracking } from "../hooks/useClarityTracking";
import { format } from "date-fns";
import useFeatureConfig from "../hooks/useFeatureConfig";

const SubMaterialModal = ({
  open,
  onClose,
  onSuccess,
  mainProductId,
  wono,
  counter,
  setCounter,
  isTimeReport = false,
  reportDate = null,
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [type, setType] = useState("");
  const [unit, setUnit] = useState("");
  const [comment, setComment] = useState("");
  const [userModifiedComment, setUserModifiedComment] = useState(false);
  const [isFetchingNote, setIsFetchingNote] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [commentError, setCommentError] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState("250px");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const config = useFeatureConfig();
  // TODO: Once time reporting is fully rolled out, remove the feature flag
  // and always include the 'wage' option.
  const optionsType = React.useMemo(() => {
    const base = [
      { value: "P", name: t("workOrder.materials.subMaterials.time") },
      {
        value: "X",
        name: t("workOrder.materials.subMaterials.productMaterials"),
      },
    ];
    if (config?.TimeReportingEnabled === true) {
      base.push({
        value: "wage",
        name: t("workOrder.materials.subMaterials.wageType"),
      });
    }
    return base;
  }, [config?.TimeReportingEnabled, t]);
  const [toastState, setToastState] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const [diaryRequired, setDiaryRequired] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [ifsUsers, setIfsUsers] = useState([]);
  const [diaryTimeCodeRequired, setDiaryTimeCodeRequired] = useState(false);

  // Wage code related state
  const [wageCodes, setWageCodes] = useState([]);
  const [wageCodeTypes, setWageCodeTypes] = useState([]);
  const [selectableWageCodes, setSelectableWageCodes] = useState([]);
  const [selectedWageCodeType, setSelectedWageCodeType] = useState(null);
  const [selectedWageCode, setSelectedWageCode] = useState(null);
  const [isWageTooltipOpen, setIsWageTooltipOpen] = useState(false);

  const [article, setArticle] = React.useState([]);
  const [showSearchProduct, setShowSearchProduct] = React.useState(false);
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const preferred_username =
    auth.userData?.profile?.preferred_username?.toUpperCase();
  const hasElevatedTappAccess = auth.userData?.profile.has_elevated_tapp_access;
  const onSeek = () => {
    setShowSearchProduct(!showSearchProduct);
  };
  const trackEvent = useClarityEventTracking();
  const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const url = `${rootUrl}WorkOrder/planning/${wono}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch most used products.");
        }

        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchUsers = async () => {
      try {
        const url = `${rootUrl}User/all`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch most used products.");
        }

        const data = await response.json();
        setIfsUsers(data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchProducts();
    hasElevatedTappAccess && !isTimeReport && fetchUsers();
    const screenHeight = window.innerHeight;
    const calculatedMaxHeight = `${screenHeight * 0.35}px`;
    setMenuMaxHeight(calculatedMaxHeight);
  }, []);

  useEffect(() => {
    const fetchDiaryRequirement = async () => {
      try {
        const url = `${rootUrl}WorkOrder/agreementRule/${wono}?ruleKey=DIARYONAGREE`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status === 204) {
          return;
        } else if (response.ok) {
          const data = await response.json();

          if (data !== null && data.ruleKey === "DIARYONAGREE") {
            setDiaryRequired(true);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchDiaryRequirement();
  }, [accessToken]);

  useEffect(() => {
    const fetchDiaryTimeCodeRequirement = async () => {
      try {
        const url = `${rootUrl}WorkOrder/agreementRule/${wono}?ruleKey=TIMECODETODIARY`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.status === 204) {
          return;
        } else if (response.ok) {
          const data = await response.json();

          if (data !== null && data.ruleKey === "TIMECODETODIARY") {
            setDiaryTimeCodeRequired(true);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    if (diaryRequired === true) {
      fetchDiaryTimeCodeRequirement();
    }
  }, [diaryRequired]);

  useEffect(() => {
    if ((!article || (article && article.length < 1)) && type === "P") {
      setArticle([
        {
          planLineNo: -1,
          workOrderCostType: "P",
          catalogNo: t("workOrder.materials.subMaterials.workingHours"),
          lineDescription: "(h)",
        },
      ]);
    } else if (
      article &&
      article.length === 1 &&
      article[0].catalogNo === "RESA INOM ARBETSTID"
    ) {
      setArticle([
        ...article,
        {
          planLineNo: -1,
          workOrderCostType: "P",
          catalogNo: t("workOrder.materials.subMaterials.workingHours"),
          lineDescription: "(h)",
        },
      ]);
    }
  }, [article]);

  useEffect(() => {
    if (type === "") return;
    const catalogNoSet = new Set();
    let filtered = [];
    if (!reportDate) {
      setSelectedDate(new Date());
    }
    if (type === "P") {
      setUnit("hour");
      if (products) {
        filtered = products.filter((product) => {
          if (product.workOrderCostType === "P" && product.catalogNo !== "") {
            if (!catalogNoSet.has(product.catalogNo)) {
              catalogNoSet.add(product.catalogNo);
              return true;
            }
          }
          return false;
        });
      }
      setArticle(filtered);
    } else if (type === "X") {
      setUnit("");
      if (products) {
        filtered = products.filter((product) => {
          if (product.workOrderCostType !== "P") {
            if (!catalogNoSet.has(product.catalogNo)) {
              catalogNoSet.add(product.catalogNo);
              return true;
            }
          }
          return false;
        });
      }
      setArticle(filtered);
    } else if (type === "wage") {
      setUnit("");
      setArticle([]);
    }
  }, [type, products]);

  // Fetch wage codes/types when wage type is selected or date changes
  useEffect(() => {
    const getWageCodes = async () => {
      try {
        const date = selectedDate
          ? format(new Date(selectedDate), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd");
        const response = await fetch(
          `${rootUrl}timereport/workorder/wagecodes?date=${date}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch wage codes");
        }
        const data = await response.json();
        setWageCodes(data.wageCodes || []);
        setWageCodeTypes(data.wageCodeTypes || []);
      } catch (err) {
        console.error("Error fetching wage codes:", err);
      }
    };

    if (type === "wage" && accessToken) {
      getWageCodes();
    }
  }, [type, selectedDate, accessToken, rootUrl]);

  // Map selectable wage codes when type changes or wage code type changes
  useEffect(() => {
    if (type !== "wage") return;
    if (selectedWageCodeType) {
      const mapped = wageCodes
        .filter((item) => item.type === selectedWageCodeType)
        .map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code,
          description: item.description,
        }));
      setSelectableWageCodes(mapped);
    } else {
      setSelectableWageCodes([]);
    }
  }, [type, selectedWageCodeType, wageCodes]);

  useEffect(() => {
    const fetchExistingNote = async () => {
      if (
        type !== "wage" ||
        !selectedDate ||
        !accessToken ||
        userModifiedComment
      ) {
        return;
      }
      try {
        setIsFetchingNote(true);
        const dateStr = format(new Date(selectedDate), "yyyy-MM-dd");
        const resp = await fetch(`${rootUrl}timereport/note?date=${dateStr}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (resp.status === 404 || resp.status === 204) {
          if (!userModifiedComment) setComment("");
          return;
        }
        if (!resp.ok) {
          return;
        }
        const data = await resp.json().catch(() => null);
        if (!userModifiedComment) {
          if (data && data.note && data.note.length > 0) {
            setComment(data.note);
          } else {
            setComment("");
          }
        }
      } catch (e) {
        /* silent */
      } finally {
        setIsFetchingNote(false);
      }
    };
    fetchExistingNote();
  }, [type, selectedDate, accessToken, rootUrl, userModifiedComment]);

  const addproduct = async () => {
    setIsProcessing(true);
    // Validate wage selections if wage type chosen
    if (type === "wage" && (!selectedWageCodeType || !selectedWageCode)) {
      setToastState({
        open: true,
        severity: "info",
        message: t("timeReport.addTimeDialog.errors.noWageCodeSelected"),
      });
      setIsProcessing(false);
      return;
    }

    let workOrderCodings = [];

    if (type === "wage") {
      // Treat wage like a material/product entry with cost type X and article = wage code
      workOrderCodings = [
        {
          cataloG_NO: selectedWageCode?.value || null,
          qtY_TO_INVOICE: quantity,
          qty: quantity,
          emP_NO: preferred_username,
          diarY_NOTE: diaryRequired === true ? comment : "",
          worK_ORDER_COST_TYPE: "X",
          comment: comment,
          crE_DATE: selectedDate
            ? selectedDate.toISOString().split("T")[0]
            : null,
        },
      ];
    } else {
      const productsToCreateRowsFor =
        selectedProducts.length > 0 ? selectedProducts : [null];

      workOrderCodings = productsToCreateRowsFor.map((productOption) => {
        const product = productOption?.value;
        const article =
          product && product.catalogNo !== "" && product.planLineNo !== -1
            ? product.catalogNo
            : null;

        return {
          cataloG_NO: article,
          qtY_TO_INVOICE: quantity,
          qty: quantity,
          emP_NO:
            type === "P"
              ? selectedUser?.value || preferred_username
              : preferred_username,
          diarY_NOTE: diaryRequired === true ? comment : "",
          worK_ORDER_COST_TYPE: type === "P" ? "P" : product?.workOrderCostType,
          comment: comment,
          crE_DATE: selectedDate
            ? selectedDate.toISOString().split("T")[0]
            : null,
        };
      });
    }

    const woData = {
      wO_NO: wono,
      performeD_ACTION_LO: diaryRequired === false ? comment : null,
      worK_ORDER_CODINGS: workOrderCodings,
    };
    await updateWorkOrder(woData);

    if (type === "P") {
      trackEvent("Lägg till tid på AO");
    } else {
      trackEvent("Lägg till produkt/material på AO");
    }
  };

  const updateWorkOrder = async (woData) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
      method: "PATCH",
      headers: headers,
      body: JSON.stringify(woData),
    };
    let url = `${rootUrl}WorkOrder/wo`;
    const response = await fetch(url, options);

    if (response.ok) {
      if (comment !== "") {
        const diaryRow = {
          woNo: wono,
          userId: preferred_username,
          createdBy: preferred_username,
          diaryText: comment,
          diaryTime: quantity,
          diaryTimestamp: selectedDate
            ? selectedDate.toISOString().split("T")[0]
            : null,
          userRole: "TEC",
          visible: true,
          timeCode:
            diaryTimeCodeRequired === true
              ? woData.worK_ORDER_CODINGS[0].cataloG_NO
              : null,
        };

        if (diaryRequired === true) {
          addNewDiaryRow(diaryRow);
        } else {
          setToastState({
            open: true,
            severity: "success",
            message: t("updateSuccess"),
          });
        }
      } else {
        setToastState({
          open: true,
          severity: "success",
          message: t("updateSuccess"),
        });
      }
      // Save time report note separately if modified
      if (type === "wage" && userModifiedComment) {
        try {
          const notePayload = {
            date: selectedDate
              ? selectedDate.toISOString().split("T")[0]
              : null,
            note: comment && comment.length > 0 ? comment.slice(0, 2000) : null,
          };
          if (notePayload.date) {
            const noteResp = await fetch(`${rootUrl}timereport/note`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify(notePayload),
            });
            if (!noteResp.ok) {
              console.error("Failed saving wage note", await noteResp.text());
              setToastState({
                open: true,
                severity: "warning",
                message: t("timeReport.addTimeDialog.errors.noteSaveError", {
                  defaultValue: "Note not saved",
                }),
              });
            }
          }
        } catch (e) {
          console.error("Error saving wage note", e);
        }
      }
      setCounter(0);
      setQuantity(1);
      setComment("");
      setUserModifiedComment(false);
      if (isTimeReport) {
        onSuccess?.({
          quantity,
          date: selectedDate ? selectedDate.toISOString().split("T")[0] : null,
          workOrderNo: wono,
        });
        onClose?.();
      }
    } else {
      setToastState({
        open: true,
        severity: "error",
        message: t("updateFail"),
      });
    }
    setIsProcessing(false);
  };

  const addNewDiaryRow = async (diaryRow) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
      method: "PATCH",
      headers: headers,
      body: JSON.stringify(diaryRow),
    };
    let url = `${rootUrl}Diary/add`;
    const response = await fetch(url, options);

    if (response.ok) {
      setToastState({
        open: true,
        severity: "success",
        message: t("updateSuccess"),
      });
    } else {
      setToastState({
        open: true,
        severity: "error",
        message: t("updateFail"),
      });
    }
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 0.25);
  };

  const decreaseQuantity = () => {
    if (quantity > 0) {
      setQuantity((prev) => prev - 0.25);
    }
  };

  const handleType = (event) => {
    if (isTimeReport) return;
    setSelectedProducts([]);
    setSelectedUser(null);
    setType(event.target.value);
    setSelectedWageCode(null);
    setSelectedWageCodeType(null);
    setComment("");
    setUserModifiedComment(false);
    setIsFetchingNote(false);
    setIsWageTooltipOpen(false);
  };

  const handleOnChangeProductTime = (event, value) => {
    if (type === "X") {
      setSelectedProducts(value || []);
      return;
    }

    setSelectedProducts(value ? [value] : []);
  };

  const handleOnChangeUser = (event, value) => {
    if (value) {
      setSelectedUser(value);
    }
  };

  const handleOnClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setSelectedProducts([]);
      setQuantity(1);
      setType("");
      setComment("");
      setUserModifiedComment(false);
      setIsFetchingNote(false);
      setIsWageTooltipOpen(false);
      setSelectedDate(new Date());
      onClose();
    }
  };

  const handleWageTooltipToggle = () => {
    setIsWageTooltipOpen((prev) => !prev);
  };

  const handleWageTooltipClose = () => {
    setIsWageTooltipOpen(false);
  };

  useEffect(() => {
    setIsWageTooltipOpen(false);
  }, [selectedWageCode]);

  useEffect(() => {
    if (open && isTimeReport) {
      setType("P");
    }
  }, [open, isTimeReport]);

  useEffect(() => {
    // When reportDate is provided, lock selectedDate to that value
    if (reportDate) {
      const dateObj =
        reportDate instanceof Date ? reportDate : new Date(reportDate);
      if (!Number.isNaN(dateObj.getTime())) {
        setSelectedDate(dateObj);
      }
    } else {
      // Fallback to "now" if no reportDate is given
      setSelectedDate(new Date());
    }
  }, [reportDate, open]);

  const isOptionEqualToValue = (option, value) => option.value === value.value;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      style={{ display: showSearchProduct ? "none" : "" }}
      slots={{ backdrop: Backdrop }}
    >
      <DialogTitle>
        {" "}
        {isTimeReport
          ? t("workOrder.materials.subMaterials.titleTime")
          : t("workOrder.materials.subMaterials.title")}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ minWidth: 220, marginTop: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="select-type">
              {t("workOrder.materials.subMaterials.selectType")}
            </InputLabel>
            <Select
              labelId="select-type"
              id="select-id-type"
              value={type}
              label={t("workOrder.materials.subMaterials.selectType")}
              onChange={handleType}
              disabled={isTimeReport}
            >
              {optionsType !== null ? (
                optionsType.map((row, key) => (
                  <MenuItem key={key} value={row.value}>
                    {row.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={0}>---</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        {type !== "wage" && (
          <Box sx={{ minWidth: 220, marginTop: 1 }}>
            <Autocomplete
              id="combo-box-product"
              multiple={type === "X"}
              disableCloseOnSelect={type === "X"}
              options={article
                .filter((article) => article.catalogNo)
                .map((article) => ({
                  value: article,
                  label: `${article.catalogNo} - ${article.lineDescription}`,
                }))}
              fullWidth
              value={
                type === "X" ? selectedProducts : selectedProducts[0] || null
              }
              ListboxProps={{ style: { maxHeight: menuMaxHeight } }}
              onChange={handleOnChangeProductTime}
              isOptionEqualToValue={isOptionEqualToValue}
              renderOption={
                type === "X"
                  ? (props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={checkboxIcon}
                          checkedIcon={checkboxCheckedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )
                  : undefined
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    type === "P"
                      ? t("workOrder.materials.subMaterials.selectSalePartTime")
                      : t("workOrder.materials.subMaterials.selectSalePart")
                  }
                  variant="outlined"
                />
              )}
            />
          </Box>
        )}

        {/* Wage type selections */}
        {type === "wage" && (
          <>
            <Box sx={{ minWidth: 220, marginTop: 1 }}>
              <Autocomplete
                id="combo-box-wage-type"
                options={wageCodeTypes}
                value={selectedWageCodeType}
                onChange={(e, val) => setSelectedWageCodeType(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.wageTypeCode")}
                    variant="outlined"
                  />
                )}
                getOptionLabel={(option) =>
                  option
                    ? t(`timeReport.addTimeDialog.wageCodeGroups.${option}`)
                    : ""
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 220,
                marginTop: 1,
              }}
            >
              <Autocomplete
                id="combo-box-wage-code"
                options={selectableWageCodes}
                value={selectedWageCode}
                onChange={(e, val) => setSelectedWageCode(val)}
                isOptionEqualToValue={isOptionEqualToValue}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.wageTypeName")}
                    variant="outlined"
                  />
                )}
              />
              <Box
                sx={{
                  width: 40,
                  display: "flex",
                  justifyContent: "center",
                  marginLeft: 1,
                }}
              >
                {selectedWageCode?.description && (
                  <ClickAwayListener onClickAway={handleWageTooltipClose}>
                    <Tooltip
                      title={selectedWageCode.description}
                      arrow
                      open={isWageTooltipOpen}
                      onClose={handleWageTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton onClick={handleWageTooltipToggle}>
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                )}
              </Box>
            </Box>
          </>
        )}
        {hasElevatedTappAccess && type === "P" && !isTimeReport && (
          <Box sx={{ minWidth: 220, marginTop: 1 }}>
            <Autocomplete
              id="combo-box-users"
              options={ifsUsers.map((user) => ({
                value: user.eltelId,
                label: `${user.name}, ${user.eltelId}`,
              }))}
              fullWidth
              value={selectedUser}
              ListboxProps={{ style: { maxHeight: menuMaxHeight } }}
              onChange={handleOnChangeUser}
              isOptionEqualToValue={isOptionEqualToValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("workOrder.materials.subMaterials.selectExecutor")}
                  variant="outlined"
                />
              )}
            />
          </Box>
        )}

        {/* Quantity Counter */}
        <Box sx={{ minWidth: 220, marginTop: 1 }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <IconButton onClick={decreaseQuantity}>
                <RemoveIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <TextField
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(0, +e.target.value))}
                style={{ width: "85px", textAlign: "center" }}
              />
            </Grid>

            <Grid item>
              <IconButton onClick={increaseQuantity}>
                <AddIcon />
              </IconButton>
            </Grid>
            {unit !== "" && (
              <Grid item sx={{ marginLeft: 2 }}>
                <DurationDisplay value={quantity} />
              </Grid>
            )}
          </Grid>
        </Box>
        <Box
          component="div"
          sx={{
            display: type === "P" || type === "wage" ? "flex" : "none",
            alignItems: "center",
            margin: "16px 0 0 0",
          }}
        >
          <DatePicker
            label={t("workOrder.materials.subMaterials.reportTime")}
            sx={{ width: "100%" }}
            value={selectedDate}
            onChange={(newValue) => {
              if (!isTimeReport) {
                setSelectedDate(newValue);
              }
            }}
            disabled={isTimeReport}
            PopperProps={{
              placement: "top-start",
            }}
          />
        </Box>
        <Box
          component="div"
          sx={{ display: type === "P" ? "flex" : "none", alignItems: "center" }}
        >
          <TextField
            label={t("workOrder.materials.subMaterials.note")}
            fullWidth
            margin="normal"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setUserModifiedComment(true);
            }}
            error={commentError}
            helperText={
              diaryRequired
                ? t("workOrder.materials.subMaterials.noteHelperTextDiary")
                : t("workOrder.materials.subMaterials.noteHelperText")
            }
          />
        </Box>
        <Box
          component="div"
          sx={{
            display: type === "wage" ? "flex" : "none",
            alignItems: "center",
          }}
        >
          <TextField
            label={t("workOrder.materials.subMaterials.note")}
            fullWidth
            margin="normal"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setUserModifiedComment(true);
            }}
            placeholder={isFetchingNote ? t("loading") : undefined}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {!isTimeReport && (
          <>
            <SearchProductModual
              open={showSearchProduct}
              onClose={onSeek}
              wono={wono}
              setToastState={setToastState}
              setCounter={setCounter}
            />

            <Button onClick={onSeek} color="primary">
              <SearchIcon />
              {t("workOrder.materials.subMaterials.searchProduct")}
            </Button>
          </>
        )}

        <Button
          onClick={addproduct}
          color="primary"
          disabled={
            isProcessing ||
            (comment.length < 1 && type === "P") ||
            (type === "X" && selectedProducts.length < 1) ||
            (type === "wage" && (!selectedWageCodeType || !selectedWageCode)) ||
            type === ""
          }
        >
          {t("add")}
        </Button>
        <Button onClick={handleOnClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Position and alignment
        style={{ position: "absolute", zIndex: 1301 }} // Adjust zIndex to be above other elements
      >
        <Alert
          severity={toastState.severity}
          variant="filled"
          onClose={() => setToastState({ ...toastState, open: false })}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SubMaterialModal;
